import isEqual from 'lodash/isEqual';
import { Action, ActionFunction0, ActionFunction1 } from 'redux-actions';
import {
  BaseSaveableWidgetState,
  BaseWidgetState,
  NetworkRequestState,
} from './baseTypes';
import { addErrorNotification } from '@wix/da-shared-react/pkg/Notifications/redux/actionCreators';
import { isApiError } from '@wix/da-http-client';
import { all, put } from 'redux-saga/effects';

function isWidgetStateDirty<T extends BaseWidgetState<any>>(
  widgetState: T
): boolean {
  return !isEqual(widgetState.currentData, widgetState.initialData);
}

export function mergeWidgetDataChange<T extends BaseWidgetState<any>>(
  widgetState: T | undefined,
  data: Partial<T['initialData']>,
  defaultData: T
): T {
  if (!widgetState) {
    return defaultData;
  }
  const updatedWidgetState = {
    ...widgetState,
    currentData: widgetState.currentData
      ? {
          ...widgetState.currentData,
          ...data,
        }
      : widgetState.initialData,
  };

  return updatedWidgetState;
}

export function mergeSaveableWidgetDataChange<
  T extends BaseSaveableWidgetState<any>,
>(
  widgetState: T,
  data: Partial<T['currentData']>,
  defaultWidgetState: T,
  getIsDirty: (widgetState: T) => boolean = isWidgetStateDirty
) {
  const updatedWidgetState = mergeWidgetDataChange(
    widgetState,
    data,
    defaultWidgetState
  );
  updatedWidgetState.isDirty = getIsDirty(updatedWidgetState);

  return updatedWidgetState;
}

export function makeSpecificWidgetDataMergeFunction<
  T extends BaseWidgetState<any>,
>(defaultWidgetState: T) {
  return (widgetState: T, data: Partial<T['currentData']>) =>
    mergeWidgetDataChange(widgetState, data, defaultWidgetState);
}

export function makeSpecificSaveableWidgetDataMergeFunction<
  T extends BaseSaveableWidgetState<any>,
>(defaultWidgetState: T, getIsDirty?: (widgetState: T) => boolean) {
  return (widgetState: T, data: Partial<T['currentData']>) =>
    mergeSaveableWidgetDataChange(
      widgetState,
      data,
      defaultWidgetState,
      getIsDirty
    );
}

export function saveRequestMetaReducer({
  initialAction,
  successAction,
  failureAction,
  clearDataOnSuccess = false,
}: {
  initialAction: ActionFunction0<any>;
  successAction: ActionFunction0<any>;
  failureAction: ActionFunction1<string, Action<{ errorMessage: string }>>;
  clearDataOnSuccess?: boolean;
}) {
  return {
    [initialAction.toString()]: (state, action) => ({
      ...state,
      saveRequest: {
        status: NetworkRequestState.SENT_AND_WAITING_FOR_RESPONSE,
      },
    }),
    [successAction.toString()]: (state, action) => ({
      ...state,
      initialData: clearDataOnSuccess ? state.initialData : state.currentData,
      currentData: clearDataOnSuccess ? state.initialData : state.currentData,
      isDirty: false,
      saveRequest: {
        status: NetworkRequestState.SUCCESS,
      },
    }),
    [failureAction.toString()]: (state, action) => ({
      ...state,
      saveRequest: {
        status: NetworkRequestState.FAILURE,
        errorMessage: action.payload.errorMessage,
      },
    }),
  };
}

export function* handleSaveAction<
  T extends {
    success: boolean;
    errorDescription?: string;
    errorDetails?: string;
  },
  SuccessAction extends ActionFunction0<any>,
  FailureAction extends ActionFunction1<
    string,
    Action<{ errorMessage: string }>
  >,
>(
  response: T,
  successAction: SuccessAction,
  failureAction: FailureAction,
  defaultErrorMessage: string,
  pushErrorNotification: boolean = true
) {
  if (isApiError(response) || !response.success) {
    const errorMessage =
      response.errorDetails ?? response.errorDescription ?? defaultErrorMessage;
    yield all([
      put(failureAction(errorMessage)),
      pushErrorNotification && put(addErrorNotification(errorMessage)),
    ]);
    return false;
  }

  yield put(successAction());
  return true;
}
